import Avatar from '@/components/linear/avatar';
import EmptyState from '@/components/linear/empty-state';
import IssueDetail from '@/components/linear/issue-detail';
import PageToolbar from '@/components/linear/page-toolbar';
import StatusIcon from '@/components/linear/status-icon';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import {
  captureAttachmentPath,
  captureDismissalPath,
  issueIdentifierPath,
  openIssuePath,
  pullRequestItemAttachmentPath,
  pullRequestItemDismissalPath,
} from '@/lib/linear-paths';
import { isTypingTarget } from '@/lib/linear-utils';
import { formatCompactTime } from '@/lib/time';
import type { Capture } from '@/types/capture';
import type { Issue } from '@/types/issue';
import type { Project } from '@/types/project';
import type { PullRequestItem } from '@/types/pull-request-item';
import { router } from '@inertiajs/react';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface InboxItem {
  id: string;
  kind: 'issue' | 'capture' | 'pull_request' | 'comment';
  title: string;
  subtitle?: string;
  createdAt: string;
  identifier?: string;
  status?: string;
  href?: string;
  userName?: string;
  avatarColor?: string;
  issue?: Issue;
  capture?: Capture;
  pullRequestItem?: PullRequestItem;
}

interface Props {
  priorityItems?: InboxItem[];
  otherItems?: InboxItem[];
  captures?: Capture[];
  pullRequestItems?: PullRequestItem[];
  projects?: Project[];
  urgentIssues?: Issue[];
  otherIssues?: Issue[];
}

export default function Index({
  priorityItems,
  otherItems,
  captures = [],
  pullRequestItems = [],
  projects = [],
  urgentIssues = [],
  otherIssues = [],
}: Props) {
  const [tab, setTab] = useState<'priority' | 'other'>('priority');
  const {
    registerItems,
    selectedIndex,
    setSelectedIndex,
    paletteOpen,
    createIssueOpen,
    shortcutsOpen,
    peekIssue,
    shortcutMenu,
  } = useWorkspaceChrome();
  const overlayOpen =
    paletteOpen || createIssueOpen || shortcutsOpen || peekIssue != null || shortcutMenu != null;

  const derivedPriority: InboxItem[] = useMemo(() => {
    if (priorityItems) return priorityItems;
    return [...urgentIssues.map(issueToItem), ...captures.map(captureToItem)];
  }, [captures, priorityItems, urgentIssues]);

  const derivedOther: InboxItem[] = useMemo(() => {
    if (otherItems) return otherItems;
    return [...otherIssues.map(issueToItem), ...pullRequestItems.map(prToItem)];
  }, [otherIssues, otherItems, pullRequestItems]);

  const rows = tab === 'priority' ? derivedPriority : derivedOther;
  const activeIndex = activeRowIndex(selectedIndex, rows.length);
  const selected = activeIndex >= 0 ? rows[activeIndex] : undefined;
  const rowsRef = useRef(rows);
  const overlayOpenRef = useRef(overlayOpen);
  const selectedIndexRef = useRef(selectedIndex);
  rowsRef.current = rows;
  overlayOpenRef.current = overlayOpen;
  selectedIndexRef.current = selectedIndex;

  useEffect(() => {
    registerItems(
      rows.map((item) => ({
        id: item.id,
        identifier: item.identifier,
        href: inboxItemHref(item) ?? undefined,
        issue: item.issue,
      }))
    );
  }, [registerItems, rows]);

  useEffect(() => {
    // Capture runs before the workspace list handler so J/K move one row, and
    // so an open palette or other overlay still receives the key.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return;
      if (isTypingTarget(event.target)) return;
      if (overlayOpenRef.current) return;

      const key = event.key.toLowerCase();
      if (key === 'j' || key === 'k') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const delta = key === 'j' ? 1 : -1;
        const next = nextRowIndex(selectedIndexRef.current, delta, rowsRef.current.length);
        selectedIndexRef.current = next;
        setSelectedIndex(next);
        return;
      }

      if (event.key !== 'Enter') return;
      if (isActivationTarget(event.target)) return;

      const list = rowsRef.current;
      if (list.length === 0) return;

      const fromRow = inboxRowIndex(event.target);
      const index =
        fromRow != null && fromRow < list.length
          ? fromRow
          : activeRowIndex(selectedIndexRef.current, list.length);
      const item = list[index];
      if (!item) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (index !== selectedIndexRef.current) {
        selectedIndexRef.current = index;
        setSelectedIndex(index);
      }
      const href = inboxItemHref(item);
      if (href) router.visit(href);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [setSelectedIndex]);

  return (
    <div className="ln-page">
      <PageToolbar
        title="Inbox"
        tabs={
          <div className="ln-tabs">
            <button
              type="button"
              className="ln-tab"
              data-active={tab === 'priority' ? 'true' : 'false'}
              onClick={() => {
                if (tab === 'priority') return;
                setTab('priority');
                setSelectedIndex(0);
              }}
            >
              Priority <span className="ln-tab-count">{derivedPriority.length}</span>
            </button>
            <button
              type="button"
              className="ln-tab"
              data-active={tab === 'other' ? 'true' : 'false'}
              onClick={() => {
                if (tab === 'other') return;
                setTab('other');
                setSelectedIndex(0);
              }}
            >
              Other <span className="ln-tab-count">{derivedOther.length}</span>
            </button>
          </div>
        }
      />
      <div className="ln-inbox-split">
        <div className="ln-inbox-list">
          {rows.length === 0 ? (
            <EmptyState title="You’re all caught up" description="No notifications." />
          ) : (
            rows.map((item, index) => (
              <InboxRow
                key={`${item.kind}-${item.id}`}
                item={item}
                index={index}
                selected={index === activeIndex}
                onSelect={() => setSelectedIndex(index)}
              />
            ))
          )}
        </div>
        <div className="ln-inbox-preview">
          {selected?.issue ? (
            <IssueDetail key={selected.issue.id} issue={selected.issue} />
          ) : selected?.capture ? (
            <div style={{ padding: 24 }}>
              <h2 className="ln-detail-title" style={{ fontSize: 18 }}>
                {selected.title}
              </h2>
              <p className="ln-inbox-sub">{inboxSubtitle(selected)}</p>
              <CaptureActions capture={selected.capture} projects={projects} />
            </div>
          ) : selected?.pullRequestItem ? (
            <div style={{ padding: 24 }}>
              <h2 className="ln-detail-title" style={{ fontSize: 18 }}>
                {selected.title}
              </h2>
              <p className="ln-inbox-sub">{inboxSubtitle(selected)}</p>
              <PrActions item={selected.pullRequestItem} projects={projects} />
            </div>
          ) : selected ? (
            <div style={{ padding: 24 }}>
              <h2 className="ln-detail-title" style={{ fontSize: 18 }}>
                {selected.identifier ? `${selected.identifier} ` : ''}
                {selected.title}
              </h2>
              <p className="ln-inbox-sub">{inboxSubtitle(selected)}</p>
              {selected.identifier ? (
                <button
                  type="button"
                  className="ln-primary-btn"
                  style={{ marginLeft: 0, marginTop: 16 }}
                  onClick={() => router.visit(issueIdentifierPath(selected.identifier!))}
                >
                  Open issue
                </button>
              ) : null}
            </div>
          ) : (
            <EmptyState title="You’re all caught up" description="Select a notification." />
          )}
        </div>
      </div>
    </div>
  );
}

function InboxRow({
  item,
  index,
  selected,
  onSelect,
}: {
  item: InboxItem;
  index: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selected) return;
    rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  return (
    <div
      ref={rowRef}
      className="ln-inbox-row"
      data-inbox-index={index}
      data-selected={selected ? 'true' : 'false'}
      onClick={onSelect}
      role="button"
      tabIndex={0}
    >
      <Avatar name={item.userName || item.title} color={item.avatarColor} size={28} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ln-inbox-title">
          {item.identifier ? <span className="ln-row-id">{item.identifier} </span> : null}
          {item.title || '\u00a0'}
        </div>
        <div className="ln-inbox-sub">{inboxSubtitle(item)}</div>
      </div>
      {item.status ? <StatusIcon status={item.status} /> : null}
      <span className="ln-row-date">{formatCompactTime(item.createdAt)}</span>
    </div>
  );
}

function CaptureActions({ capture, projects }: { capture: Capture; projects: Project[] }) {
  const [projectId, setProjectId] = useState(capture.suggestedProjectId ?? '');

  return (
    <div
      style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}
      onClick={(event) => event.stopPropagation()}
    >
      <select
        className="ln-filter-input"
        value={projectId}
        onChange={(event) => setProjectId(event.target.value)}
        aria-label="Attach to project"
      >
        <option value="">Choose project</option>
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="ln-primary-btn"
        style={{ marginLeft: 0 }}
        disabled={!projectId}
        onClick={() => router.post(captureAttachmentPath(capture.id), { projectId })}
      >
        Attach
      </button>
      <button
        type="button"
        className="ln-toolbar-btn"
        onClick={() => router.post(captureDismissalPath(capture.id))}
      >
        Dismiss
      </button>
    </div>
  );
}

function PrActions({ item, projects }: { item: PullRequestItem; projects: Project[] }) {
  const [projectId, setProjectId] = useState(item.suggestedProjectId ?? '');

  return (
    <div
      style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}
      onClick={(event) => event.stopPropagation()}
    >
      <select
        className="ln-filter-input"
        value={projectId}
        onChange={(event) => setProjectId(event.target.value)}
        aria-label="Attach PR to project"
      >
        <option value="">Choose project</option>
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="ln-primary-btn"
        style={{ marginLeft: 0 }}
        disabled={!projectId}
        onClick={() => router.post(pullRequestItemAttachmentPath(item.id), { projectId })}
      >
        Attach
      </button>
      <button
        type="button"
        className="ln-toolbar-btn"
        onClick={() => router.post(pullRequestItemDismissalPath(item.id))}
      >
        Dismiss
      </button>
    </div>
  );
}

function inboxRowIndex(target: EventTarget | null): number | null {
  if (!(target instanceof HTMLElement)) return null;
  const row = target.closest('[data-inbox-index]');
  if (!(row instanceof HTMLElement)) return null;
  const index = Number(row.dataset.inboxIndex);
  if (!Number.isInteger(index) || index < 0) return null;
  return index;
}

function inboxSubtitle(item: InboxItem): string {
  const subtitle = item.subtitle?.trim();
  if (subtitle) return subtitle;
  if (item.kind === 'capture') return 'Untriaged capture';
  if (item.kind === 'pull_request') return 'Untriaged pull request';
  if (item.kind === 'comment') return 'Comment';
  if (item.issue?.assignee) return `Assigned to ${item.issue.assignee.name}`;
  return 'Notification';
}

function inboxItemHref(item: InboxItem): string | null {
  if (item.href) return item.href;
  if (item.issue) return openIssuePath(item.issue);
  if (item.identifier) return issueIdentifierPath(item.identifier);
  return null;
}

function activeRowIndex(selectedIndex: number, length: number): number {
  if (length === 0) return -1;
  if (selectedIndex < 0 || selectedIndex >= length) return 0;
  return selectedIndex;
}

function nextRowIndex(current: number, delta: number, length: number): number {
  if (length === 0) return -1;
  const base = activeRowIndex(current, length);
  const next = base + delta;
  if (next >= length) return 0;
  if (next < 0) return length - 1;
  return next;
}

function isActivationTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'BUTTON' || tag === 'A';
}

function issueToItem(issue: Issue): InboxItem {
  return {
    id: issue.id,
    kind: 'issue',
    title: issue.title,
    identifier: issue.identifier,
    subtitle: issue.assignee ? `Assigned to ${issue.assignee.name}` : 'Unassigned',
    createdAt: issue.updatedAt ?? issue.createdAt,
    status: issue.status,
    issue,
  };
}

function captureToItem(capture: Capture): InboxItem {
  return {
    id: capture.id,
    kind: 'capture',
    title: capture.excerpt.slice(0, 80) || 'Capture',
    subtitle: 'Untriaged capture',
    createdAt: capture.createdAt,
    capture,
  };
}

function prToItem(item: PullRequestItem): InboxItem {
  return {
    id: item.id,
    kind: 'pull_request',
    title: item.title,
    subtitle: `${item.repoFullName}#${item.number}`,
    createdAt: item.createdAt,
    pullRequestItem: item,
  };
}
